import React, {Fragment, useEffect, useRef, useState} from "react";
import styled from "styled-components";
import {
	RegistrationGigyaModal,
	LoginGigyaModal,
	RegistrationModal,
	ButtonPrimaryAlt,
	Exist,
	LandingContainer,
	LandingContentBox,
	LandingLogoLink,
	ButtonSecondaryAlt,
} from "components";
import logoDark from "assets/img/fantlogo-light.svg";
import bgLandingGradient from "assets/img/bg-landing-gradient.svg";
import bgLoginMobileGradient from "assets/img/bg-login-mobile-gradient.svg";
import bgLoginMobile from "assets/img/bg-login-mobile-v3.jpg";
import bgLanding from "assets/img/bg-landing.jpg";
import golfers from "assets/img/golfers.png";
import golfersMobile from "assets/img/golfers-mobile.png";
import {useSelector} from "react-redux";
import {getErrorModalMessage, isAdditionalDataRequired} from "modules/selectors";
import {IS_SECRET_PASSED} from "modules";

const LoginLayoutContainer = styled.div`
	--bgLandingImage: url(${bgLanding});
	--bgImage: url(${golfers});
	--bgGradientImage: url(${bgLandingGradient});

	--bgMobileImage: url(${bgLoginMobile});
	--bgMobileGradientImage: url(${bgLoginMobileGradient});
	--bgImageMobile: url(${golfersMobile});

	--asideGradientLayer: var(--bgGradientImage) no-repeat right top / 50% 100%;
	--golfersLayer: var(--bgImage) no-repeat left bottom / auto 100%;
	--bgLayer: var(--bgLandingImage) no-repeat left top / cover;

	background: var(--asideGradientLayer), var(--golfersLayer),
		var(--bgLayer);

	flex: 1;
	min-height: 878px;
	display: flex;
	flex-flow: column;

	@media (max-width: 1260px) {
		--asideGradientLayer: var(--bgGradientImage) no-repeat right top / 60% 100%;
		--golfersLayer: var(--bgImage) no-repeat right 75% bottom 0 / auto 100%;
	}

	@media (max-width: 1000px) {
		--asideGradientLayer: var(--bgGradientImage) no-repeat right top / 70% 100%;
	}

	@media (max-width: 840px) {
		--bgGradientImage: var(--bgMobileGradientImage);
		--bgImage: var(--bgImageMobile);

		--bgLayer: var(--bgLandingImage) no-repeat left top / 150% 50%;
		--asideGradientLayer: var(--bgGradientImage) no-repeat left bottom / 100% 95%;
		--golfersLayer: var(--bgImage) no-repeat center top calc(40% - 28vw) / 60% auto;
	}

	@media (max-width: 600px) {
		--golfersLayer: var(--bgImage) no-repeat center top calc(27% - 28vw) / 100% auto;
		min-height: 920px;
	}
`;

const LoginLayoutContainerSecrete = styled(LoginLayoutContainer)`
	@media (max-width: 840px) {
		--golfersLayer: var(--bgImage) no-repeat center top calc(90% - 28vw) / 60% auto;
		--bgLayer: var(--bgLandingImage) no-repeat left top / 150% 60%;
		--asideGradientLayer: var(--bgGradientImage) no-repeat left bottom / 100% 75%;
		min-height: 600px;
	}

	@media (max-width: 600px) {
		--golfersLayer: var(--bgImage) no-repeat center top calc(50% - 28vw) / 100% auto;
		--bgLayer: var(--bgLandingImage) no-repeat left top / 150% 60%;
		--asideGradientLayer: var(--bgGradientImage) no-repeat left bottom / 100% 65%;
		min-height: 740px;
	}
`;

const Buttons = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;

	@media (max-width: 840px) {
		flex-direction: row;
		gap: 8px;
	}
`;

const Button = styled(ButtonPrimaryAlt)`
	width: 100%;
	--btnBackground: #1e1450;
	--btnHoverBackground: #453d79;
`;

const ButtonSecondary = styled(ButtonSecondaryAlt)`
	width: 100%;
	--btnTextColor: #fff;
	--btnHoverBackground: #1e1450;
	--btnBorderColor: #fff;
`;

const Title = styled.h3`
	margin-bottom: 20px;

	@media (max-width: 840px) {
		font-size: 22px;
		line-height: 24px;
	}
`;

const SubTitle = styled.p`
	margin-bottom: 20px;
	font-size: 20px;
	line-height: 32px;

	@media (max-width: 840px) {
		font-size: 16px;
		line-height: 26px;
	}
`;

const Notice = styled.p`
	font-size: 12px;
	line-height: 16px;
	margin-top: 22px;
	color: #fff;
	text-align: left;
	font-weight: 700;

	@media (max-width: 840px) {
		text-align: center;
	}
`;

export const Login: React.FC = () => {
	const ref = useRef<HTMLDivElement>(null);
	const isRegistrationRequired = useSelector(isAdditionalDataRequired);
	const hasGlobalError = Boolean(useSelector(getErrorModalMessage));

	const [isLoginVisible, showLoginModal] = useState(false);
	const [isRegistrationVisible, showRegistrationModal] = useState(false);
	const showLogin = () => showLoginModal(true);
	const closeLogin = () => showLoginModal(false);
	const showRegistration = () => showRegistrationModal(true);
	const closeRegistration = () => showRegistrationModal(false);

	useEffect(() => {
		// Hide Gigya modals if registration required
		if (isRegistrationRequired || hasGlobalError) {
			showLoginModal(false);
			showRegistrationModal(false);
		}
	}, [isRegistrationRequired, hasGlobalError]);

	useEffect(() => {
		const element = ref.current;

		if (element && !IS_SECRET_PASSED) {
			let count = 0;

			element.addEventListener("click", () => {
				count++;

				if (count >= 10) {
					sessionStorage.setItem("isSecretPassed", "true");
					window.location.reload();
				}
			});
		}
	}, [ref]);

	return IS_SECRET_PASSED ? (
		<Fragment>
			<LoginLayoutContainer>
				<LandingContainer>
					<LandingLogoLink>
						<img src={logoDark} alt="DP World Tour Fantasy" />
					</LandingLogoLink>
					<LandingContentBox>
						<Title>
							Welcome to the Official
							<br />
							Fantasy Game of the DP
							<br />
							World Tour
						</Title>
						<SubTitle>
						Pick six players every week of the 2024 season and be in with a chance of winning incredible prizes all season long. Feel closer to the action on the DP World Tour where every week counts, and compete against golf fans from around the world.
						</SubTitle>
						<Buttons>
							<Button onClick={showLogin}>Play Now</Button>
							<ButtonSecondary onClick={showRegistration}>Register</ButtonSecondary>
						</Buttons>
						<Notice>
							PLEASE READ: IMPORTANT INFORMATION
							<br />
							To play Fantasy DP World Tour in 2024, please log in with your My Tour account or create a new My Tour account. If you played in 2023, you can login using the same details used last year. If you face any issues, please contact {" "}
							<a href="mailto:fanengagement@europeantourgroup.com">
								fanengagement@europeantourgroup.com
							</a>
						</Notice>
					</LandingContentBox>
				</LandingContainer>
			</LoginLayoutContainer>
			<Exist when={isLoginVisible}>
				<LoginGigyaModal onClose={closeLogin} />
			</Exist>
			<Exist when={isRegistrationVisible}>
				<RegistrationGigyaModal onClose={closeRegistration} />
			</Exist>
			<Exist when={isRegistrationRequired}>
				<RegistrationModal />
			</Exist>
		</Fragment>
	) : (
		<LoginLayoutContainerSecrete>
			<LandingContainer>
				<LandingLogoLink ref={ref}>
					<img src={logoDark} alt="DP World Tour Fantasy" />
				</LandingLogoLink>
				<LandingContentBox>
					<Title>
						Welcome to the official Fantasy game of the DP World Tour
					</Title>
					<SubTitle>
						We are busy working on the launch of the 2024 season. Come back in few hours to pick your six players for the first tournament of the 2024 season and be in with a chance of winning incredible prizes all season long.
					</SubTitle>
				</LandingContentBox>
			</LandingContainer>
		</LoginLayoutContainerSecrete>
	);
};

export default Login;
